import type { DataType } from "@repo/lanes";

import { rift } from "../rift";
import type { ColumnsConfig, FilterType, RiftConfig } from "../types";
import { escapeIdentifier } from "./escape-identifier";
import { escapeLiteral } from "./escape-literal";
import {
  addRelativeInterval,
  filterToSql,
  getIntervalSql,
  getRelativeWindowBounds,
  getRelativeWindowSql,
  type RelativeWindowOptions,
  toTimestampLiteral,
} from "./filter-to-sql";
import { type StatEntry, statsSql } from "./stats-sql";

/**
 * Returns whether a filter targets a bounded date range (RELATIVE or BETWEEN).
 * Only these modes define a window that can be shifted to a previous period.
 */
export const isDateRangeFilter = (filter: FilterType): boolean =>
  filter.mode === "RELATIVE" || filter.mode === "BETWEEN";

/**
 * Generates a WHERE clause for the previous period of a single date filter.
 *
 * For RELATIVE filters the window is shifted back by its own duration:
 * - PREVIOUS `[lower, upper)` becomes `[lower - duration, upper - duration)`
 * - CURRENT and NEXT follow the same shift logic.
 *
 * For BETWEEN filters `{start, end}` the previous period is
 * `[start - (end - start), start)`.
 *
 * Returns `null` for non-date filters or incomplete date filters.
 */
export const previousPeriodFilterToSql = (
  filter: FilterType,
  opts?: RelativeWindowOptions,
): string | null => {
  if (filter.mode === "RELATIVE") {
    if (!filter.relative) return null;

    const amount = Math.max(1, Math.floor(filter.relative.value));

    let prevLower: string;
    let prevUpper: string;

    if (opts?.now) {
      // Local-anchored: shift the concrete window back by its own duration and
      // emit literals so the previous period matches what the user sees.
      const bounds = getRelativeWindowBounds(filter.relative, opts.now);
      if (!bounds) return null;
      prevLower = toTimestampLiteral(
        addRelativeInterval(bounds.start, -amount, filter.relative.interval),
      );
      prevUpper = toTimestampLiteral(
        addRelativeInterval(bounds.end, -amount, filter.relative.interval),
      );
    } else {
      const window = getRelativeWindowSql(filter.relative);
      if (!window) return null;

      const shiftInterval = getIntervalSql(amount, filter.relative.interval);
      prevLower = `(${window.lowerBound}) - ${shiftInterval}`;
      prevUpper = `(${window.upperBound}) - ${shiftInterval}`;
    }

    const parts = filter.columns.map((column) => {
      const col = `CAST(${escapeIdentifier(column)} AS TIMESTAMP)`;
      return `(${col} >= ${prevLower} AND ${col} < ${prevUpper})`;
    });

    if (parts.length === 0) return null;
    return parts.length > 1 ? `(${parts.join(" AND ")})` : (parts[0] ?? null);
  }

  if (filter.mode === "BETWEEN") {
    const range = filter.value as { start?: string; end?: string } | null;
    if (!range?.start || !range?.end) return null;

    const start = escapeLiteral(range.start);
    const end = escapeLiteral(range.end);

    const parts = filter.columns.map((column) => {
      const col = `CAST(${escapeIdentifier(column)} AS TIMESTAMP)`;
      // DuckDB's `DATE - DATE` yields BIGINT, but only `-(DATE, INTEGER)` binds.
      return `(${col} >= ${start}::DATE - CAST(${end}::DATE - ${start}::DATE AS INTEGER) AND ${col} < ${start}::DATE)`;
    });

    if (parts.length === 0) return null;
    return parts.length > 1 ? `(${parts.join(" AND ")})` : (parts[0] ?? null);
  }

  return null;
};

/**
 * Builds a complete stats SQL query against the previous period.
 *
 * All date-range board filters (RELATIVE / BETWEEN) are shifted to their
 * previous period of equal duration. Non-date filters are applied unchanged.
 * The inner rift SQL (groups, aggregates, block-level filters, sorts, limit)
 * is preserved, and the result is wrapped with `statsSql()` for aggregation.
 *
 * Returns `null` when:
 * - `boardFilters` contains no date-range filters
 * - `entries` is empty
 */
export const previousPeriodStatsSql = (
  tableNameStr: string,
  riftConfig: RiftConfig | null,
  boardFilters: FilterType[],
  entries: StatEntry[],
  columnDefs?: Record<string, DataType> | null,
  columnsConfig?: ColumnsConfig | null,
  opts?: RelativeWindowOptions,
): string | null => {
  if (entries.length === 0) return null;

  const hasDateFilters = boardFilters.some(isDateRangeFilter);
  if (!hasDateFilters) return null;

  const whereParts: string[] = [];
  for (const f of boardFilters) {
    if (isDateRangeFilter(f)) {
      const prevSql = previousPeriodFilterToSql(f, opts);
      if (prevSql) whereParts.push(prevSql);
    } else {
      whereParts.push(filterToSql(f, opts));
    }
  }

  if (whereParts.length === 0) return null;
  const boardWhere = whereParts.join(" AND ");

  const baseSql = rift(
    tableNameStr,
    riftConfig,
    null,
    columnDefs,
    columnsConfig,
    opts,
  );

  const escapedTableName = `'${tableNameStr}'`;
  const innerSql = `WITH _prev_board_filtered AS (\n  SELECT * FROM ${escapedTableName} WHERE ${boardWhere}\n)\n${baseSql.replaceAll(escapedTableName, "_prev_board_filtered")}`;

  return statsSql(innerSql, entries, riftConfig);
};
